// The everyday add of each board kind, in the command bar (Features 4, Tidy): "new slide" on a slides board, "new
// page" on a doc, a deal, a post, a task. Each loads its board's code only when run, as the board itself does (the
// pipeline's sheets are already loaded, for the command bar's deals).

import { CopyPlus, SquarePlus, Calendar1, CalendarCheck, CalendarCog, CalendarDays, CalendarPlus, CalendarRange, CalendarX, ChevronLeft, ChevronRight, FilePlus2, Handshake, List, PanelLeft, Plus, SlidersHorizontal } from "lucide-react";
import { copyBoard } from "../../state/actions";
import { usePipeUi } from "../../boards/pipeline/state";
import { addCommands, type Command } from "./registry";

addCommands("kinds", ({ board, kind }): Command[] => {
  if (!board || board.archived) return [];
  const id = board.id;
  switch (kind) {
    case "custom":
      return [
        { id: "kind:duplicate", group: "This board", label: "Duplicate this board", hint: "Its app and its records, as a board of its own", icon: <CopyPlus size={15} />, keywords: "duplicate copy clone custom board records", run: () => void copyBoard(id, true) },
        { id: "kind:new-from", group: "This board", label: "New board from this", hint: "Its app, with no records", icon: <SquarePlus size={15} />, keywords: "new board from this template copy clone custom app blank", run: () => void copyBoard(id, false) },
      ];
    case "doc":
      return [{ id: "kind:new-page", group: "This board", label: "New page", hint: `A page in ${board.name}`, icon: <FilePlus2 size={15} />, keywords: "new page add doc write note", run: () => void import("../../boards/doc/pages").then((m) => m.newPage(id)) }];
    case "slides":
      return [
        {
          id: "kind:new-slide",
          group: "This board",
          label: "New slide",
          hint: "After the slide you are on",
          kbd: "Enter",
          icon: <Plus size={15} />,
          keywords: "new slide add slide deck page",
          run: () => void Promise.all([import("../../boards/slides/deck"), import("../../boards/slides/state")]).then(([d, st]) => d.newSlide(id, st.useSlidesUi.getState().open[id] ?? null)),
        },
      ];
    case "pipeline":
      return [
        { id: "kind:new-deal", group: "This board", label: "New deal", hint: "A client or a lead", icon: <Handshake size={15} />, keywords: "new deal client lead add customer", run: () => usePipeUi.setState({ sheet: "new", newIn: null, open: null }) },
        // its columns and its currency, in one window (Fixes 9)
        { id: "kind:stages", group: "This board", label: "Stages and currency", hint: "The columns, and the currency values are in", icon: <SlidersHorizontal size={15} />, keywords: "stages columns currency money pounds euros dollars gbp eur usd values settings", run: () => usePipeUi.setState({ sheet: "stages" }) },
      ];
    case "calendar":
      return [
        {
          id: "kind:new-post",
          group: "This board",
          label: "New post",
          hint: "On today",
          kbd: "N",
          icon: <CalendarPlus size={15} />,
          keywords: "new post add content write schedule",
          run: () => void Promise.all([import("../../boards/calendar/posts"), import("../../boards/calendar/state")]).then(([p, st]) => p.newPost(id, { day: st.today() })),
        },
      ];
    case "agenda": {
      // the Calendar's own: the box where an event is typed in words, the views, today, back and on, its column
      const ui = () => import("../../boards/agenda/state");
      const step = (n: number) => () => void Promise.all([ui(), import("../../../shared/agenda")]).then(([st, a]) => {
        const s = st.useAgendaUi.getState();
        st.setAt(id, a.stepView(s.kept[id]?.view ?? "week", s.at[id] ?? st.today(), n));
      });
      const view = (v: "month" | "week" | "day" | "list") => () => void ui().then((st) => st.setView(id, v));
      // the calendars column brought out, then a control in it pressed or given the focus
      const inColumn = (sel: string, how: "click" | "focus") => () =>
        void ui().then((st) => {
          st.setKept(id, { side: true });
          setTimeout(() => {
            const el = document.querySelector<HTMLButtonElement>(sel);
            if (!el) return;
            el.scrollIntoView({ block: "nearest" });
            if (how === "click") el.click();
            else el.focus();
          }, 120);
        });
      return [
        { id: "kind:new-event", group: "This board", label: "New event", hint: "Like “Lunch with Sam Friday 1pm 1h”", kbd: "N", icon: <CalendarPlus size={15} />, keywords: "new event add meeting appointment calendar schedule book", run: () => void import("../../boards/agenda/AgendaView").then((m) => m.focusNew(id)) },
        { id: "kind:agenda-today", group: "This board", label: "Go to today", hint: "On the Calendar", kbd: "T", icon: <CalendarCheck size={15} />, keywords: "today now this week calendar", run: () => void ui().then((st) => st.setAt(id, st.today())) },
        { id: "kind:agenda-month", group: "This board", label: "Show the month", kbd: "M", icon: <CalendarDays size={15} />, keywords: "month view calendar", run: view("month") },
        { id: "kind:agenda-week", group: "This board", label: "Show the week", kbd: "W", icon: <CalendarRange size={15} />, keywords: "week view calendar", run: view("week") },
        { id: "kind:agenda-day", group: "This board", label: "Show one day", kbd: "D", icon: <Calendar1 size={15} />, keywords: "day view calendar", run: view("day") },
        { id: "kind:agenda-list", group: "This board", label: "Show as a list", hint: "Four weeks, day by day", kbd: "L", icon: <List size={15} />, keywords: "list agenda schedule upcoming view calendar", run: view("list") },
        { id: "kind:agenda-next", group: "This board", label: "Go to the next week or month", kbd: "→", icon: <ChevronRight size={15} />, keywords: "next forward later calendar", run: step(1) },
        { id: "kind:agenda-back", group: "This board", label: "Go back a week or month", kbd: "←", icon: <ChevronLeft size={15} />, keywords: "previous back earlier calendar", run: step(-1) },
        { id: "kind:agenda-side", group: "This board", label: "Show or hide the calendars", hint: "The column down the left", icon: <PanelLeft size={15} />, keywords: "calendars column sidebar panel mini month", run: () => void ui().then((st) => st.setKept(id, { side: !(st.useAgendaUi.getState().kept[id]?.side ?? true) })) },
        // Fixes 9: where new events go, and taking a Google calendar off, both in the calendars column
        { id: "kind:agenda-dest", group: "This board", label: "Choose where new events go", hint: "Tick the Google calendars", icon: <CalendarCog size={15} />, keywords: "default calendar new events google board where destination save to", run: inColumn(".agd-cals-dest .agd-into", "click") },
        { id: "kind:agenda-remove", group: "This board", label: "Remove a Google calendar", hint: "Or disconnect its account", icon: <CalendarX size={15} />, keywords: "remove delete disconnect google calendar account unlink", run: inColumn(".agd-cal-x, .agd-acct-off", "focus") },
      ];
    }
  }
  return [];
});
